import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleExternalToggle);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.refs.dialog?.addEventListener(
      'click',
      this.#handleBackdropClick
    );
    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#lockPageScroll
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#unlockPageScroll
    );
    this.#syncExternalToggle();
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
    
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleExternalToggle);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.refs.dialog?.removeEventListener(
      'click',
      this.#handleBackdropClick
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#lockPageScroll
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#unlockPageScroll
    );
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#syncExternalToggle
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#syncExternalToggle
    );
    this.#historyAbortController?.abort();
  }
  #handleExternalToggle = (event) => {
  const target =
    event.target instanceof Element
      ? event.target
      : null;

  const button = target?.closest('[data-cart-drawer-toggle]');

  if (!button) return;

  const controlledDialogId = button.getAttribute('aria-controls');
  const dialog = /** @type {HTMLDialogElement | undefined} */ (
    this.refs.dialog
  );

  if (!dialog) return;

  if (
    controlledDialogId &&
    dialog.id &&
    controlledDialogId !== dialog.id
  ) {
    return;
  }

  event.preventDefault();

  if (dialog.open) {
    this.close();
  } else {
    this.open();
  }
};

#syncExternalToggle = () => {
  const dialog = /** @type {HTMLDialogElement | undefined} */ (
    this.refs.dialog
  );

  if (!dialog) return;

  document
    .querySelectorAll('[data-cart-drawer-toggle]')
    .forEach((button) => {
      const controlledDialogId =
        button.getAttribute('aria-controls');

      if (
        controlledDialogId &&
        dialog.id &&
        controlledDialogId !== dialog.id
      ) {
        return;
      }

      button.setAttribute(
        'aria-expanded',
        dialog.open ? 'true' : 'false'
      );
    });
};
  #lockPageScroll = () => {
    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('cart-drawer-open');
  };

  #unlockPageScroll = () => {
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');
  };
  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      // this.showDialog();
      this.showDrawerDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }
  showDrawerDialog() {
      const dialog = /** @type {HTMLDialogElement | undefined} */ (
        this.refs.dialog
      );

      if (!dialog || dialog.open) return;

      dialog.show();
      this.dispatchEvent(new DialogOpenEvent());
  }
  open() {
    // this.showDialog();
    this.showDrawerDialog();
    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
  #handleBackdropClick = (event) => {
    const dialog = this.refs.dialog;
    const inner = event.target.closest('.cart-drawer__inner');

    if (!dialog || event.target !== dialog || inner) return;

    this.close();
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
